import { useCallback, useMemo } from "react";
import { ScrollView } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { DataStateView, EmptyStateProps } from "@src/components/screen-state";
import { useCameras } from "@src/features/cameras";
import { groupCamerasBySite } from "@src/features/cameras/utils/camera-group";
import { useSiteNames } from "@src/features/cameras/hooks/useSiteNames";
import { isLegacyAppCamera } from "@src/utils/key-parser";
import { SiteCard } from "@src/components/SiteCard";
import { useFocusEffect } from "@react-navigation/native";
import { isWeb } from "@src/utils/web-helpers";

interface SiteSelectorProps {
  onSitePress: (siteId: string) => void;
}

export function SiteSelector({ onSitePress }: SiteSelectorProps) {
  const { theme } = useUnistyles();
  const { data: cameras, refetch: refetchCameras, isLoading, isFetching, error } = useCameras();
  const siteNames = useSiteNames();

  useFocusEffect(
    useCallback(() => {
      refetchCameras();
    }, [refetchCameras]),
  );

  const supportedCameras = useMemo(() => {
    if (!cameras) return [];
    return cameras.filter((camera) => !isLegacyAppCamera(camera));
  }, [cameras]);

  const siteGroups = useMemo(() => {
    if (!supportedCameras) return [];
    return groupCamerasBySite(supportedCameras, "site", siteNames);
  }, [supportedCameras, siteNames]);

  const hasCamerasButOnlyLegacy = useMemo(() => {
    return cameras && cameras.length > 0 && supportedCameras.length === 0;
  }, [cameras, supportedCameras]);

  const emptyState = useMemo((): EmptyStateProps => {
    if (hasCamerasButOnlyLegacy) {
      return {
        icon: "videoOff",
        title: "Upgrade device fleet",
        message:
          "The new and improved alarm schedule management system, including the AI Schedule Assistant, is only available for the new fleet of devices. Get in touch with your account manager to discuss upgrading your device fleet.",
      };
    }
    return {
      icon: "videoOff",
      title: "No cameras found",
      message: "There are no cameras available to configure alarm settings.",
    };
  }, [hasCamerasButOnlyLegacy]);

  return (
    <DataStateView
      data={cameras}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      refetch={refetchCameras}
      isEmpty={() => supportedCameras.length === 0}
      loadingMessage="Loading alarms..."
      errorTitle="Unable to load alarms"
      emptyState={emptyState}
      showStaleDataBanner={false}
    >
      {() => (
        <ScrollView
          showsVerticalScrollIndicator={false}
          style={{ flex: 1 }}
          contentContainerStyle={{
            paddingHorizontal: theme.spacing["3xl"],
            flexGrow: 1,
            paddingTop: theme.spacing["4xl"],
            paddingBottom: theme.spacing["12xl"],
          }}
        >
          <View gap="3xl">
            <View gap="xxs">
              <Text textStyle="md/medium">Select a Site</Text>
              <Text textStyle="sm/regular" color="textMuted">
                Choose a site below to view and manage its alarm settings.
              </Text>
            </View>

            <View
              style={
                isWeb ?
                  { flexDirection: "row", flexWrap: "wrap", gap: theme.spacing["3xl"] }
                : { gap: theme.spacing["3xl"] }
              }
            >
              {siteGroups.map((group) => (
                <SiteCard key={group.siteId} group={group} isSelected={false} onPress={onSitePress} />
              ))}
            </View>
          </View>
        </ScrollView>
      )}
    </DataStateView>
  );
}
